import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import useCanonicalUrl from '@/hooks/useCanonicalUrl';
import Layout from '@/components/layout/Layout';
import apiService from '@/services/api';
import { AlertCircle } from 'lucide-react';

const CookiesPage = () => {
  useCanonicalUrl();
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCookiePolicy = async () => {
      try {
        const response = await apiService.get('/public/legal/cookie_policy');
        if (response?.success && response.data?.content) {
          setContent(response.data.content);
        } else {
          setError('Cookie policy content not found in database');
        }
      } catch (err) {
        setError('Failed to load cookie policy. Please try again later.');
        console.error('Cookie policy error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCookiePolicy();
  }, []);

  if (loading) {
    return (
      <Layout>
        <Helmet>
          <title>Cookie Policy | Virewire Property Services</title>
          <meta name="description" content="Learn about how we use cookies on the Virewire website." />
        </Helmet>
        <section className="section-padding">
          <div className="container-custom text-center py-12">
            <p className="text-muted-foreground">Loading cookie policy...</p>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <Helmet>
        <title>Cookie Policy | Virewire Property Services</title>
        <meta name="description" content="Learn about how we use cookies on the Virewire website." />
      </Helmet>

      <section className="section-padding">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h1 className="text-3xl md:text-4xl font-bold text-primary mb-8">Cookie Policy</h1>
            
            {error ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex gap-3 items-start mb-8">
                <AlertCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-red-800 font-medium">Error Loading Content</p>
                  <p className="text-red-700 text-sm">{error}</p>
                  <p className="text-red-700 text-xs mt-2">
                    Please configure via Admin Dashboard → CMS Pages
                  </p>
                </div>
              </div>
            ) : (
              <div 
                className="prose prose-lg max-w-none"
                dangerouslySetInnerHTML={{ __html: content }}
              />
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default CookiesPage;
